import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, Factory } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getVerticals, setVertical as apiSetVertical } from "@/lib/api";
import { toast } from "sonner";

export function VerticalSwitcher() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [verticals, setVerticals] = useState<any[]>([]);
  const [busy, setBusy] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getVerticals().then((d: any) => setVerticals(d.verticals || [])).catch(() => setVerticals([]));
  }, []);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const current = user?.vertical || "grocery";
  const currentName = user?.vertical_config?.name || "Grocery";

  const choose = async (vid: string) => {
    if (vid === current) { setOpen(false); return; }
    setBusy(vid);
    try {
      await apiSetVertical(vid);
      await refresh();
      toast.success("Industry switched");
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not switch industry");
    } finally { setBusy(""); }
  };

  if (!verticals.length) {
    return (
      <span data-testid="vertical-badge" className="hidden sm:inline-flex items-center rounded-full border border-ai/40 bg-ai/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ai">
        {currentName}
      </span>
    );
  }

  return (
    <div className="relative hidden sm:block" ref={ref}>
      <button
        data-testid="vertical-badge"
        onClick={() => setOpen((o) => !o)}
        title="Switch industry"
        className="inline-flex items-center gap-1 rounded-full border border-ai/40 bg-ai/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ai hover:bg-ai/20 transition-colors"
      >
        {currentName}
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid="vertical-switcher-menu" className="absolute left-0 mt-2 w-64 rounded-lg border bg-card shadow-lg z-50 p-1.5 animate-fade-up">
          <div className="flex items-center gap-1.5 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <Factory className="h-3 w-3" /> Switch industry
          </div>
          {verticals.map((v) => {
            const on = v.id === current;
            return (
              <button
                key={v.id}
                data-testid={`vertical-switch-${v.id}`}
                onClick={() => choose(v.id)}
                disabled={!!busy}
                className={`w-full flex items-center justify-between gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors disabled:opacity-50 ${on ? "bg-ai/10 text-foreground" : "text-foreground/80 hover:bg-accent"}`}
              >
                <span className="truncate">{busy === v.id ? "Switching…" : v.name}</span>
                {on && <Check className="h-4 w-4 text-ai shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default VerticalSwitcher;
